import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import DataTable from "../../components/admin/DataTable";
import AdminDialog from "../../components/admin/AdminDialog";
import { Plus } from "lucide-react";

export default function AdminSellers() {
  const [sellers, setSellers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ shop_name: "", owner_name: "", phone: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sellers/admin/all");
      setSellers(data);
    } catch (e) {
      toast.error("Failed to load sellers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createSeller = async () => {
    const shop = createForm.shop_name.trim();
    const owner = createForm.owner_name.trim();
    const phone = createForm.phone.trim();
    if (shop.length < 2) return toast.error("Shop name is required");
    if (!owner) return toast.error("Owner name is required");
    if (!/^\d{10}$/.test(phone)) return toast.error("Enter a valid 10-digit phone");
    setCreating(true);
    try {
      await api.post("/admin/sellers", { shop_name: shop, owner_name: owner, phone });
      toast.success("Seller created");
      setCreateOpen(false);
      setCreateForm({ shop_name: "", owner_name: "", phone: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create seller");
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (id, status) => {
    if (!window.confirm(`Change seller status to ${status}?`)) return;
    try {
      await api.put(`/admin/sellers/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const openDetail = async (s) => {
    try {
      const { data } = await api.get(`/admin/sellers/${s.id}`);
      setDetail(data);
    } catch { toast.error("Failed to load seller"); }
  };

  const updateCommission = async () => {
    const pct = Number(window.prompt("New commission percent (0-50):", detail.seller.commission_percent ?? 10));
    if (Number.isNaN(pct) || pct < 0 || pct > 50) return toast.error("Invalid percent");
    try {
      await api.put(`/admin/sellers/${detail.seller.id}/commission`, { commission_percent: pct });
      toast.success("Commission updated");
      await openDetail(detail.seller);
      load();
    } catch { toast.error("Failed"); }
  };

  const columns = [
    {
      key: "shop", label: "Shop",
      render: (r) => <div><div className="font-medium">{r.shop_name}</div><div className="text-xs text-[hsl(var(--kb-muted))]">{r.shop_slug}</div></div>,
    },
    { key: "status", label: "Status", render: (r) => <span className={`uppercase text-xs ${r.status === "approved" ? "text-green-700" : r.status === "rejected" ? "text-red-700" : "text-[hsl(var(--kb-primary))]"}`}>{r.status}</span> },
    { key: "commission_percent", label: "Commission %", render: (r) => `${r.commission_percent ?? 10}%` },
    { key: "gstin", label: "GSTIN", render: (r) => r.gstin || "—" },
    {
      key: "actions", label: "",
      render: (r) => (
        <div className="flex gap-2 flex-wrap">
          <button className="kb-btn-outline !py-2 !px-3 !text-xs" onClick={() => openDetail(r)} data-testid={`view-${r.shop_slug}`}>View</button>
          {r.status !== "approved" && <button className="kb-btn-primary !py-2 !px-3 !text-xs" onClick={() => updateStatus(r.id, "approved")} data-testid={`approve-${r.shop_slug}`}>Approve</button>}
          {r.status !== "rejected" && <button className="kb-btn-outline !py-2 !px-3 !text-xs" onClick={() => updateStatus(r.id, "rejected")} data-testid={`reject-${r.shop_slug}`}>Reject</button>}
          {r.status === "approved" && <button className="kb-btn-outline !py-2 !px-3 !text-xs" onClick={() => updateStatus(r.id, "suspended")}>Suspend</button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="admin-sellers-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Verification</div>
          <h1 className="font-display text-4xl">Sellers</h1>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="kb-btn-primary !py-2 !px-4 inline-flex items-center gap-2"
          data-testid="add-seller-btn"
        >
          <Plus size={16} /> Add Seller
        </button>
      </div>

      <DataTable columns={columns} rows={sellers} loading={loading} emptyLabel="No sellers yet" testid="sellers-table" />

      {createOpen && (
        <AdminDialog
          open={createOpen}
          onClose={() => !creating && setCreateOpen(false)}
          title="Add Seller"
          subtitle="Create a new approved seller account on this storefront"
          maxWidth="max-w-md"
          testid="create-seller-dialog"
        >
          <div className="space-y-4">
            <div>
              <label className="overline block mb-1">Shop Name</label>
              <input
                className="kb-input w-full"
                placeholder="Glow Mart Outlet"
                value={createForm.shop_name}
                onChange={(e) => setCreateForm({ ...createForm, shop_name: e.target.value })}
                data-testid="create-seller-shop-name"
              />
            </div>
            <div>
              <label className="overline block mb-1">Owner Name</label>
              <input
                className="kb-input w-full"
                placeholder="Arif Khan"
                value={createForm.owner_name}
                onChange={(e) => setCreateForm({ ...createForm, owner_name: e.target.value })}
                data-testid="create-seller-owner-name"
              />
            </div>
            <div>
              <label className="overline block mb-1">Phone</label>
              <input
                className="kb-input w-full"
                placeholder="10-digit phone"
                maxLength={10}
                value={createForm.phone}
                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value.replace(/\D/g, "") })}
                data-testid="create-seller-phone"
              />
              <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1">
                The seller will log in with this phone via OTP.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setCreateOpen(false)}
                className="kb-btn-outline !py-2 !px-4"
                disabled={creating}
                data-testid="create-seller-cancel"
              >
                Cancel
              </button>
              <button
                onClick={createSeller}
                className="kb-btn-primary !py-2 !px-4"
                disabled={creating}
                data-testid="create-seller-submit"
              >
                {creating ? "Creating…" : "Create Seller"}
              </button>
            </div>
          </div>
        </AdminDialog>
      )}

      {detail && (
        <AdminDialog
          open={!!detail}
          onClose={() => setDetail(null)}
          title={detail.seller.shop_name}
          subtitle="Atelier"
          maxWidth="max-w-2xl"
          testid="seller-detail-panel"
        >
          <section>
            <div className="overline mb-3">Contact</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><b>Name:</b> {detail.user?.name || "—"}</div>
              <div><b>Phone:</b> {detail.user?.phone}</div>
              <div><b>Email:</b> {detail.user?.email || "—"}</div>
              <div><b>Status:</b> <span className="uppercase">{detail.seller.status}</span></div>
            </div>
          </section>
          <section>
            <div className="overline mb-3">Compliance (KYC)</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><b>Legal Name:</b> {detail.seller.legal_name || "—"}</div>
              <div><b>Business Type:</b> {detail.seller.business_type || "—"}</div>
              <div><b>GSTIN:</b> {detail.seller.gstin || "—"}</div>
              <div><b>PAN:</b> {detail.seller.pan || "—"}</div>
            </div>
          </section>
          <section>
            <div className="overline mb-3">Bank</div>
            {detail.seller.bank ? (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><b>Holder:</b> {detail.seller.bank.account_holder}</div>
                <div><b>Bank:</b> {detail.seller.bank.bank_name || "—"}</div>
                <div><b>IFSC:</b> {detail.seller.bank.ifsc}</div>
                <div><b>A/c:</b> {detail.seller.bank.account_number}</div>
              </div>
            ) : <div className="text-sm text-[hsl(var(--kb-muted))]">Bank details not submitted</div>}
          </section>
          <section>
            <div className="overline mb-3">Commission</div>
            <div className="flex items-center justify-between text-sm">
              <div>{detail.seller.commission_percent ?? 10}%</div>
              <button onClick={updateCommission} className="kb-btn-outline !py-2 !px-3 !text-xs" data-testid="edit-commission">Edit</button>
            </div>
          </section>
          <section>
            <div className="overline mb-3">Performance</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><b>Products:</b> {detail.performance.products_count}</div>
              <div><b>Orders:</b> {detail.performance.orders_count}</div>
              <div><b>Gross:</b> ₹{detail.performance.gross_sales}</div>
              <div><b>Payout:</b> ₹{detail.performance.seller_payout}</div>
            </div>
          </section>
        </AdminDialog>
      )}
    </div>
  );
}
